import type { AnalyticsModel, AnalyticsObject } from './schema';

export type DataSourceId = 'oaa' | 'stripe' | 'bi';

/** A source system of the menu's Data group, with an overview page at `/data/source/:id`. */
export interface DataSource {
  id: DataSourceId;
  /** Menu label. */
  label: string;
  /** Page title. */
  title: string;
  summary: string;
  /** Data explorer schemas in menu order; with several, each is listed under its own label. */
  schemas: string[];
}

export const DATA_SOURCES: readonly DataSource[] = [
  {
    id: 'oaa',
    label: 'OAA',
    title: 'Open as App data',
    summary:
      'Product tables (api), copies of the production database and the legacy usage tables (dbo).',
    schemas: ['api', 'dbo'],
  },
  {
    id: 'stripe',
    label: 'Stripe',
    title: 'Stripe data',
    summary: 'Billing objects synced from Stripe every day: customers, subscriptions, invoices.',
    schemas: ['stripe'],
  },
  {
    // App Insights events and their rollups (bi), with the loader's state (etl).
    id: 'bi',
    label: 'BI',
    title: 'BI events data',
    summary:
      'App events from Application Insights, their daily and monthly rollups, and the loader.',
    schemas: ['bi', 'etl'],
  },
];

export function findDataSource(id: string | undefined): DataSource | undefined {
  return DATA_SOURCES.find((source) => source.id === id);
}

/** The source whose schemas include the object's, if the Data group lists it. */
export function dataSourceOf(object: Pick<AnalyticsObject, 'schema'>): DataSource | undefined {
  return DATA_SOURCES.find((source) => source.schemas.includes(object.schema));
}

export interface SourceSection {
  key: string;
  label: string;
  objects: AnalyticsObject[];
}

/**
 * A source's objects by section, as the menu lists them: one per schema, and one per subgroup in a
 * schema split into several (dbo: Production, Usage).
 */
export function sourceSections(model: AnalyticsModel, source: DataSource): SourceSection[] {
  return source.schemas
    .flatMap((schema) => model.findGroup(schema) ?? [])
    .flatMap((group) =>
      group.subgroups && group.subgroups.length > 1
        ? group.subgroups.map((subgroup) => ({
            key: `${group.schema}-${subgroup.label}`,
            label: subgroup.label,
            objects: subgroup.objects,
          }))
        : [{ key: group.schema, label: group.label, objects: group.objects }],
    );
}
